import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Archive } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useBestellungen } from '@/hooks/useBestellungen';
import { useDashboardSettings } from '@/hooks/useDashboardSettings';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { DashboardTable } from '@/components/dashboard/DashboardTable';
import { DashboardKarten } from '@/components/dashboard/DashboardKarten';
import { DashboardSearchInput } from '@/components/dashboard/DashboardSearchInput';
import { useIstSchmal } from '@/hooks/useLayout';
import { DEFAULT_VISIBLE_COLUMNS } from '@/lib/dashboardColumns';
import { anzahl } from '@/lib/format';
import type { DashboardSearch } from '@/types/dashboard';

// ---------------------------------------------------------------------
// Das Archiv (E11, A10.4).
//
// Erledigtes gehört nicht ins Arbeitsbrett, aber es gehört auch nicht
// weggeworfen. Wer eine Bestellung von vor einem Jahr sucht — weil die
// Kundin nachfragt, weil eine Rechnung geprüft wird —, findet sie hier.
//
// OHNE FRIST. Der frühere Weg dorthin war ein Filterschalter
// „Archivierte einbeziehen" mit einem Rückblick von 90 Tagen. Damit war
// eine Bestellung von vor einem Jahr unauffindbar, und genau das ist der
// Zweck eines Archivs.
//
// DIESELBEN BAUSTEINE WIE DAS DASHBOARD, absichtlich: Tabelle, Karten
// und Suchfeld sind dieselben Komponenten. Eine nachgebaute Archiv-
// Tabelle hätte dieselben Spalten ein zweites Mal geführt und wäre beim
// nächsten Umbau am Dashboard zurückgeblieben (Designprinzip 4).
//
// WAS HIER BEWUSST FEHLT: Die Filterleiste, die Herkunfts-Reiter, der
// Scanner, das Erfassungs-Blatt. Im Archiv wird gesucht, nicht
// gearbeitet — und die Zählzeile des Dashboards beschreibt den
// Arbeitsvorrat des Ladens, zu dem Erledigtes nicht gehört.
// ---------------------------------------------------------------------

const archivSearchSchema = z.object({
  q: z.coerce.string().optional(),
  sort: z.string().optional()
});

function ArchivSeite() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const istSchmal = useIstSchmal();
  const auth = useAuth();
  // Dieselbe Spaltenwahl wie im Dashboard, absichtlich: wer dort eine
  // Spalte abgeschaltet hat, will sie hier auch nicht sehen.
  const { settings } = useDashboardSettings(auth.data?.user.id ?? null, {
    visibleColumns: DEFAULT_VISIBLE_COLUMNS
  });

  const suche: DashboardSearch = {
    q: search.q,
    sort: search.sort,
    archiv: 'nur'
  };
  const list = useBestellungen(suche);
  const data = list.data;

  // `ersetzen` fürs Tippen im Suchfeld: kein Verlaufsschritt je
  // Buchstabe (wie im Dashboard, Befund 29.10.2026).
  function patchSearch(patch: Partial<typeof search>, ersetzen = false): void {
    void navigate({
      replace: ersetzen,
      search: ((prev: typeof search) => {
        const next = { ...prev, ...patch };
        for (const k of Object.keys(next) as (keyof typeof next)[]) {
          if (next[k] === undefined || next[k] === '') delete next[k];
        }
        return next;
      }) as never
    });
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className={
          istSchmal
            ? 'flex flex-col gap-2 border-b border-line bg-cream px-3 py-2'
            : 'flex flex-wrap items-center gap-3 border-b border-line bg-cream px-4 py-3'
        }
      >
        <h1 className="flex shrink-0 items-center gap-2 font-serif text-lg text-ink">
          <Archive className="h-5 w-5 text-ink-soft" aria-hidden />
          Archiv
        </h1>
        <div className={istSchmal ? '' : 'min-w-0 flex-1'}>
          <DashboardSearchInput
            value={search.q ?? ''}
            onChange={(v) => patchSearch({ q: v }, true)}
            onSubmit={() => undefined}
            // Kein Scanner im Archiv: ein Zettel in der Hand führt über
            // die Nummern-Auflösung des Dashboards direkt zur Bestellung,
            // und die findet Archivierte längst.
            onScanOeffnen={() => undefined}
            isLoading={list.isFetching}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" data-scroll-restoration-id="archiv-liste">
        {list.isError ? (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <p className="text-coral">Fehler beim Laden des Archivs.</p>
            <Button variant="secondary" onClick={() => void list.refetch()}>
              Erneut versuchen
            </Button>
          </div>
        ) : data === undefined ? (
          <div className="flex items-center justify-center p-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : data.bestellungen.length === 0 ? (
          // Der Unterschied macht die Auskunft: „noch nichts archiviert"
          // ist etwas anderes als „zu dieser Suche nichts gefunden".
          <div className="p-12 text-center text-sm text-ink-faint">
            {search.q && search.q.trim() !== ''
              ? `Keine archivierte Bestellung zu „${search.q.trim()}" gefunden.`
              : 'Es ist noch nichts archiviert.'}
          </div>
        ) : istSchmal ? (
          <DashboardKarten zeilen={data.bestellungen} />
        ) : (
          <DashboardTable
            zeilen={data.bestellungen}
            visibleColumns={settings.visibleColumns}
            sort={search.sort ?? 'dringlichkeit'}
            onSortChange={(s) => patchSearch({ sort: s === 'dringlichkeit' ? undefined : s })}
          />
        )}
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-x-4 border-t border-line bg-cream-warm px-4 py-1 text-xs text-ink-soft">
        <span className="tabular-nums">
          {anzahl(data?.total ?? 0, 'archivierte Bestellung', 'archivierte Bestellungen')}
        </span>
      </footer>
    </div>
  );
}

export const Route = createFileRoute('/archiv')({
  validateSearch: archivSearchSchema,
  component: ArchivSeite
});
